import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { CC_CHART_TERMS } from './ccGoTerms';

// Bar chart of CC GO terms (x) vs. number of proteins associated (y).
// Clicking a bar toggles that term in `selectedGoIds`.
export default function CcGoTermBarChart({
  rowData,
  selectedGoIds,
  onToggle,
  isDark,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(0);

  const counts = useMemo(() => {
    const map = new Map(CC_CHART_TERMS.map(t => [t.id, 0]));
    if (!Array.isArray(rowData)) return map;
    for (const row of rowData) {
      const ccTerms = Array.isArray(row.gotermCcAll) ? row.gotermCcAll : [];
      const seen = new Set();
      for (const t of ccTerms) {
        const id = typeof t === 'string' ? t : (t?.go_id || t?.id || '');
        if (id && map.has(id) && !seen.has(id)) {
          map.set(id, map.get(id) + 1);
          seen.add(id);
        }
      }
    }
    return map;
  }, [rowData]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setWidth(el.clientWidth || 0);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current || width <= 0) return;

    const data = CC_CHART_TERMS.map(t => ({
      id: t.id,
      label: t.label,
      count: counts.get(t.id) || 0,
    }));

    // Left margin must accommodate the rotated x-axis label of the FIRST bar,
    // which extends up-and-left past the y-axis. Right margin is bumped by the
    // same amount so the plotting area stays visually centered.
    const margin = { top: 26, right: 56, bottom: 130, left: 70 };
    const W = width;
    const H = 320;
    const innerW = Math.max(0, W - margin.left - margin.right);
    const innerH = Math.max(0, H - margin.top - margin.bottom);

    const x = d3.scaleBand()
      .domain(data.map(d => d.id))
      .range([0, innerW])
      .padding(0.2);

    const yMax = d3.max(data, d => d.count) || 1;
    const y = d3.scaleLinear()
      .domain([0, yMax])
      .nice()
      .range([innerH, 0]);

    const fg = isDark ? '#ddd' : '#222';
    const grid = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
    const barFill = isDark ? '#5fa8ff' : '#2a6fc7';
    const barFillSelected = d3.interpolateRgb(barFill, '#fff')(isDark ? 0.45 : 0.55);
    const barFillHover = isDark ? '#7fbcff' : '#1f5aa8';

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${W} ${H}`)
       .attr('width', '100%')
       .attr('height', H);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Y-axis with light gridlines
    const yAxis = d3.axisLeft(y).ticks(5).tickSizeOuter(0);
    const yAxisG = g.append('g').call(yAxis);
    yAxisG.selectAll('text').attr('fill', fg).attr('font-size', 11);
    yAxisG.selectAll('line').attr('stroke', fg).attr('stroke-opacity', 0.4);
    yAxisG.selectAll('path').attr('stroke', fg).attr('stroke-opacity', 0.4);

    g.append('g')
      .attr('class', 'cc-grid')
      .selectAll('line')
      .data(y.ticks(5))
      .join('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', d => y(d))
      .attr('y2', d => y(d))
      .attr('stroke', grid);

    // Y-axis label
    g.append('text')
      .attr('transform', `translate(${-margin.left + 14},${innerH / 2}) rotate(-90)`)
      .attr('text-anchor', 'middle')
      .attr('fill', fg)
      .attr('font-size', 11)
      .text('# proteins');

    // Non-zero bars get a minimum height so low-count terms stay visible.
    const MIN_BAR_H = 3;
    const barHeight = d => (d.count <= 0 ? 0 : Math.max(MIN_BAR_H, innerH - y(d.count)));
    const barY = d => innerH - barHeight(d);

    // Bars (visual only). Interaction is handled by the full-height hit areas
    // below, so clicking does not depend on how tall the bar is.
    const bars = g.append('g').selectAll('rect.cc-bar')
      .data(data)
      .join('rect')
      .attr('class', 'cc-bar')
      .attr('x', d => x(d.id))
      .attr('y', barY)
      .attr('width', x.bandwidth())
      .attr('height', barHeight)
      .attr('fill', d => selectedGoIds.has(d.id) ? barFillSelected : barFill)
      .attr('stroke', d => selectedGoIds.has(d.id) ? fg : 'none')
      .attr('stroke-width', d => selectedGoIds.has(d.id) ? 1.5 : 0)
      .style('pointer-events', 'none');

    // Full-height transparent hit areas: the entire column is clickable, so a
    // 5-protein bar is as easy to click as a 445-protein one.
    const hit = g.append('g').selectAll('rect.cc-hit')
      .data(data)
      .join('rect')
      .attr('class', 'cc-hit')
      .attr('x', d => x(d.id))
      .attr('y', 0)
      .attr('width', x.bandwidth())
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mouseover', (_event, d) => {
        bars.filter(b => b.id === d.id)
          .attr('fill', selectedGoIds.has(d.id) ? barFillSelected : barFillHover);
      })
      .on('mouseout', (_event, d) => {
        bars.filter(b => b.id === d.id)
          .attr('fill', selectedGoIds.has(d.id) ? barFillSelected : barFill);
      })
      .on('click', (_event, d) => {
        onToggle(d.id, !selectedGoIds.has(d.id));
      });

    hit.append('title').text(d => `${d.label} (${d.id})\n${d.count} proteins\nClick to ${selectedGoIds.has(d.id) ? 'remove' : 'apply'} filter`);

    // Count labels above bars
    g.append('g').selectAll('text.cc-count')
      .data(data)
      .join('text')
      .attr('class', 'cc-count')
      .attr('x', d => x(d.id) + x.bandwidth() / 2)
      .attr('y', d => barY(d) - 6)
      .attr('text-anchor', 'middle')
      .attr('fill', fg)
      .attr('font-size', 11)
      .attr('font-weight', 600)
      .style('pointer-events', 'none')
      .text(d => d.count);

    // X-axis: rotated labels (label + GO id), clickable
    const xAxisG = g.append('g')
      .attr('transform', `translate(0,${innerH})`);

    xAxisG.append('line')
      .attr('x1', 0).attr('x2', innerW)
      .attr('stroke', fg).attr('stroke-opacity', 0.4);

    const tickG = xAxisG.selectAll('g.cc-tick')
      .data(data)
      .join('g')
      .attr('class', 'cc-tick')
      .attr('transform', d => `translate(${x(d.id) + x.bandwidth() / 2},6)`)
      .style('cursor', 'pointer')
      .on('click', (_event, d) => {
        onToggle(d.id, !selectedGoIds.has(d.id));
      });

    tickG.append('text')
      .attr('transform', 'rotate(-40)')
      .attr('text-anchor', 'end')
      .attr('dy', '0.35em')
      .attr('fill', fg)
      .attr('font-size', 11)
      .attr('font-weight', d => selectedGoIds.has(d.id) ? 700 : 400)
      .text(d => d.label);

    tickG.append('text')
      .attr('transform', 'rotate(-40) translate(0, 13)')
      .attr('text-anchor', 'end')
      .attr('dy', '0.35em')
      .attr('fill', fg)
      .attr('fill-opacity', 0.6)
      .attr('font-size', 9)
      .text(d => d.id);
  }, [counts, width, isDark, selectedGoIds, onToggle]);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg ref={svgRef} style={{ display: 'block' }} />
    </div>
  );
}
